/**
 * Unified Reader Types and Interfaces
 *
 * These types are used across all reader components to provide
 * a consistent API regardless of book format (PDF, EPUB, HTML)
 */

export enum BookFormat {
  Pdf = 'PDF',
  Epub = 'EPUB',
  CustomHtml = 'CustomHtml'
}

/**
 * Table of Contents Item
 */
export interface TOCItem {
  id: string;
  label: string;
  href?: string;
  pageNumber?: number;
  cfi?: string; // Canonical Fragment Identifier for EPUB
  level: number; // Nesting level (0 = top-level, 1 = nested, etc.)
  children?: TOCItem[];
}

/**
 * Bookmark
 */
export interface Bookmark {
  id: string;
  bookId: string;
  userId: string;
  location: string | number; // Page number, chapter ID, or CFI
  locationLabel: string; // Human-readable label (e.g., "Page 42", "Chapter 3")
  note?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Note Type
 */
export enum NoteType {
  Text = 'text',    // Note on selected text (highlight)
  Page = 'page'     // Page-level note (no text selection)
}

/**
 * Note/Highlight
 */
export interface Note {
  id: string;
  bookId: string;
  userId: string;
  location: string | number;
  locationLabel: string;
  noteType: NoteType; // Type of note (text or page)
  selectedText?: string; // Optional - only for text notes
  color: HighlightColor;
  noteText?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Highlight Colors (Kortext-style palette)
 */
export enum HighlightColor {
  Yellow = '#FFEB3B',
  Green = '#4CAF50',
  Blue = '#2196F3',
  Pink = '#E91E63',
  Purple = '#9C27B0',
  Orange = '#FF9800',
  Red = '#F44336',
  Teal = '#009688',
  Lime = '#CDDC39',
  Amber = '#FFC107'
}

/**
 * Search Result
 */
export interface SearchResult {
  id: string;
  excerpt: string;
  location: string | number;
  locationLabel: string;
  beforeContext?: string;
  afterContext?: string;
}

/**
 * Book Metadata
 */
export interface BookMetadata {
  id: string;
  title: string;
  author?: string;
  publisher?: string;
  isbn?: string;
  coverImageUrl?: string;
  description?: string;
  publishedDate?: string;
  pageCount?: number;
  language?: string;
  format: BookFormat;
}

/**
 * Reading Progress
 */
export interface ReadingProgress {
  bookId: string;
  userId: string;
  currentLocation: string | number;
  currentLocationLabel: string;
  progressPercentage: number; // 0-100
  totalReadingTime: number; // in seconds
  lastReadAt: Date;
}

/**
 * Sidebar Tab Types
 */
export type SidebarTab = 'search' | 'toc' | 'bookmarks' | 'notes' | 'flashcards' | 'print' | 'download' | 'info' | 'settings' | 'layout' | 'quickAction' | null;

/**
 * Navigation Abstraction
 *
 * This interface provides a unified navigation API that works
 * across all book formats by abstracting away format-specific
 * location representations.
 */
export interface NavigationState {
  currentLocation: string | number;
  currentLocationLabel: string; // Human-readable (e.g., "Page 42", "Chapter 3")
  totalLocations: number;
  progressPercentage: number;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;

  /**
   * Real page number (1-based) derived from book.locations.
   * Undefined while locations are still generating on large EPUBs,
   * or for formats that don't expose page counts.
   */
  currentPage?: number;

  /**
   * End-of-visible-range page number (1-based). Equals currentPage in Single /
   * Scrolling modes; in Spread mode it's typically currentPage + 1 so the
   * bottom toolbar can render "Pages 6–7 / 815" rather than a single number.
   * Undefined when currentPage is undefined.
   */
  currentEndPage?: number;

  /**
   * Total page count derived from book.locations. Undefined until generation completes.
   */
  totalPages?: number;

  /**
   * Effective EPUB layout mode (Single / Spread / Scrolling) after Auto resolution.
   * Only populated by the EPUB renderer; undefined for PDF / HTML.
   */
  effectiveLayoutMode?: LayoutMode.Single | LayoutMode.Spread | LayoutMode.Scrolling;
}

/**
 * Navigation Callbacks
 */
export interface NavigationCallbacks {
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onNavigateToLocation: (location: string | number) => void;
  onNavigateToTOC: (tocItem: TOCItem) => void;
  onNavigateToBookmark: (bookmark: Bookmark) => void;
  onNavigateToNote: (note: Note) => void;
  onNavigateToSearchResult: (result: SearchResult) => void;
}

/**
 * Reader Actions
 */
export interface ReaderActions {
  onCreateBookmark: (location: string | number, note?: string) => Promise<void>;
  onDeleteBookmark: (bookmarkId: string) => Promise<void>;
  onUpdateBookmark: (bookmarkId: string, note: string) => Promise<void>;
  onCreateNote: (location: string | number, selectedText: string, color: HighlightColor, noteText?: string) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
  onUpdateNote: (noteId: string, noteText: string) => Promise<void>;
  onSearch: (query: string) => Promise<SearchResult[]>;
}

/**
 * Zoom Controls (PDF-specific)
 */
export interface ZoomControls {
  zoomLevel: number; // percentage (e.g., 100)
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
}

/**
 * Unified Reader Layout Props
 */
export interface UnifiedReaderLayoutProps {
  // Book Information
  bookId: string;
  format: BookFormat;
  metadata: BookMetadata;

  // Navigation State
  navigation: NavigationState;
  navigationCallbacks: NavigationCallbacks;

  // Content & Features
  tableOfContents?: TOCItem[];
  bookmarks?: Bookmark[];
  notes?: Note[];

  // Actions
  actions: ReaderActions;
  onAddPageNote?: (noteText: string, color: HighlightColor) => void;

  // Content Renderer (format-specific)
  children: React.ReactNode;

  // Floating Actions (FABs, etc.) - rendered outside content area
  floatingActions?: React.ReactNode;

  // Optional Settings
  theme?: 'light' | 'dark';
  fontSize?: number;
  fontFamily?: string;

  // Optional Zoom Controls (PDF format only)
  zoomControls?: ZoomControls;

  /**
   * SecureContentChunkId (Guid) for the chapter/page currently on screen.
   *
   * Threaded down to FlashcardPanel → FlashcardLibraryTab so that the Library
   * tab can show chapter-scoped AI-generated flashcards for exactly what the
   * student is reading. `null` is a valid state: when the reader hasn't yet
   * resolved a chunk Guid for the current location (e.g., the student hasn't
   * searched in this book yet and we have no chapterIndex → chunkId mapping),
   * the Library tab renders a friendly "Open a chapter to see its flashcard
   * library." placeholder. Each renderer owns the derivation of this value.
   */
  activeSecureContentChunkId?: string | null;
}

/**
 * Reader Settings
 */
export interface ReaderSettings {
  theme: 'light' | 'dark';
  fontSize: number; // in px
  fontFamily: string;
  lineHeight: number;
  backgroundColor: string;
  textColor: string;
  autoHideToolbar: boolean;
  autoHideDelay: number; // in ms
  useBookStyles: boolean; // If true, use original book styles; if false, apply custom settings
  layoutMode: LayoutMode;
  pageAnimation: PageAnimation;
  pageMargin: PageMargin;
  tapZoneMode: TapZoneMode;
}

/**
 * Layout enums — mirror backend values exactly.
 * Integer values MUST match learnleaf.ReaderSettings.*Mode/Animation/Margin enums.
 */
export enum LayoutMode {
  Auto = 0,
  Single = 1,
  Spread = 2,
  Scrolling = 3,
}

export enum PageAnimation {
  Slide = 0,
  Fade = 1,
  None = 2,
}

export enum PageMargin {
  Medium = 0,
  Narrow = 1,
  Wide = 2,
}

export enum TapZoneMode {
  Auto = 0,
  On = 1,
  Off = 2,
}
